import React from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import "@/index.css";
import "@/fonts/greatvibes.css";
import AuditDashboard from "@/pages/AuditDashboard";
import SharedTransaction from "@/pages/SharedTransaction";
import PrintBinder from "@/pages/PrintBinder";
import DraftPackage from "@/pages/DraftPackage";
import RespaNotice from "@/pages/RespaNotice";
import FundingCommitment from "@/pages/FundingCommitment";
import MasterFundingAgreement from "@/pages/MasterFundingAgreement";
import TaxCreditCertificate from "@/pages/TaxCreditCertificate";
import ForbearanceAgreement from "@/pages/ForbearanceAgreement";
import TroMotion from "@/pages/TroMotion";
import CollateralMotion from "@/pages/CollateralMotion";
import MotionPacket from "@/pages/MotionPacket";
import TreasuryRepo from "@/pages/TreasuryRepo";
import PrintTreasury from "@/pages/PrintTreasury";
import FederalAssistanceGrant from "@/pages/FederalAssistanceGrant";
import PrintFederalAssistance from "@/pages/PrintFederalAssistance";
import GsaBondPackage from "@/pages/GsaBondPackage";
import CounterpartyReport from "@/pages/CounterpartyReport";
import MailingLabels from "@/pages/MailingLabels";
import PrintGsaBond from "@/pages/PrintGsaBond";
import PrintGsaClaim from "@/pages/PrintGsaClaim";
import LossMitigationPackage from "@/pages/LossMitigationPackage";
import PrintLossMitigation from "@/pages/PrintLossMitigation";
import MortgageReplacement from "@/pages/MortgageReplacement";
import PrintMortgageReplacement from "@/pages/PrintMortgageReplacement";
import TaxCreditTransfer from "@/pages/TaxCreditTransfer";
import PrintTaxCredit from "@/pages/PrintTaxCredit";
import TaxCreditClient from "@/pages/TaxCreditClient";
import TaxDebtProgram from "@/pages/TaxDebtProgram";
import TaxFlyer from "@/pages/TaxFlyer";
import FciuFlyer from "@/pages/FciuFlyer";
import VerifyCertificate from "@/pages/VerifyCertificate";
import FraudReport from "@/pages/FraudReport";
import Investigations from "@/pages/Investigations";
import FinancialCrimesUnit from "@/pages/FinancialCrimesUnit";
import CasePortal from "@/pages/CasePortal";
import AdminTaxProgram from "@/pages/AdminTaxProgram";
import MemberCertificate from "@/pages/MemberCertificate";
import PrintCertificate from "@/pages/PrintCertificate";
import PrintMotion from "@/pages/PrintMotion";
import NoteSaleBinder from "@/pages/NoteSaleBinder";
import AboutUs from "@/pages/AboutUs";
import CertificateOfAuthority from "@/pages/CertificateOfAuthority";
import PrivateBankCharter from "@/pages/PrivateBankCharter";
import PrivateBankerPortal from "@/pages/PrivateBankerPortal";
import VerifyInstrument from "@/pages/VerifyInstrument";
import VerifyNoteSale from "@/pages/VerifyNoteSale";
import DebtConsolidationAgreement from "@/pages/DebtConsolidationAgreement";
import App from "@/App";
import AdminBinder from "@/pages/AdminBinder";
import MedallionVerify from "@/pages/MedallionVerify";
import RedeemDraft from "@/pages/RedeemDraft";
import BrokerBinder from "@/pages/BrokerBinder";
import ClosingBinder from "@/pages/ClosingBinder";
import BorrowerCopyPrint from "@/pages/BorrowerCopyPrint";
import PrintBundle from "@/pages/PrintBundle";
import SecondaryMarket from "@/pages/SecondaryMarket";
import BorrowerPortal from "@/pages/BorrowerPortal";
import BrokerPortal from "@/pages/BrokerPortal";
import SecuritiesPortal from "@/pages/SecuritiesPortal";
import SecuritiesVerify from "@/pages/SecuritiesVerify";
import SecuritiesLoanSign from "@/pages/SecuritiesLoanSign";
import SecuritiesMarketplace from "@/pages/SecuritiesMarketplace";
import SecuritiesBuyerDashboard from "@/pages/SecuritiesBuyerDashboard";
import SecuritiesResale from "@/pages/SecuritiesResale";
import SecuritiesInstrument from "@/pages/SecuritiesInstrument";
import RootGate from "@/pages/RootGate";
import PublicFundingRegistry from "@/pages/PublicFundingRegistry";
import PublicFundingRegister from "@/pages/PublicFundingRegister";
import PublicFundingVerify from "@/pages/PublicFundingVerify";
import { AuthProvider } from "@/auth/AuthContext";
import RequireAdmin from "@/auth/RequireAdmin";
import CallBadge from "@/components/CallBadge";
import AdminLogin from "@/auth/AdminLogin";
import ForgotPassword from "@/auth/ForgotPassword";
import ResetPassword from "@/auth/ResetPassword";
import AdminDashboard from "@/auth/AdminDashboard";
import { Toaster } from "@/components/ui/sonner";
import CounterpartyAssistant from "@/components/CounterpartyAssistant";

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/" element={<RootGate />} />
          <Route path="/workbench" element={<RequireAdmin><App /></RequireAdmin>} />
          <Route path="/audit" element={<RequireAdmin><AuditDashboard /></RequireAdmin>} />
          <Route path="/market" element={<SecondaryMarket />} />
          <Route path="/borrower" element={<BorrowerPortal />} />
          <Route path="/broker" element={<BrokerPortal />} />
          <Route path="/securities" element={<SecuritiesPortal />} />
          <Route path="/securities/verify/:token" element={<SecuritiesVerify />} />
          <Route path="/securities/loan-sign/:loanId" element={<SecuritiesLoanSign />} />
          <Route path="/securities/marketplace" element={<SecuritiesMarketplace />} />
          <Route path="/securities/my-tradelines" element={<SecuritiesBuyerDashboard />} />
          <Route path="/securities/resale/:token" element={<SecuritiesResale />} />
          <Route path="/securities/instrument/:id" element={<SecuritiesInstrument />} />
          <Route path="/funding" element={<PublicFundingRegistry />} />
          <Route path="/funding/register/:token" element={<PublicFundingRegister />} />
          <Route path="/funding/verify/:token" element={<PublicFundingVerify />} />
          <Route path="/shared/:tx_id" element={<SharedTransaction />} />
          <Route path="/print/:tx_id" element={<PrintBinder />} />
          <Route path="/verify/:token" element={<MedallionVerify />} />
          <Route path="/redeem/:token" element={<RedeemDraft />} />
          <Route path="/broker/binder/:appId" element={<BrokerBinder />} />
          <Route path="/draft-package" element={<RequireAdmin><DraftPackage /></RequireAdmin>} />
          <Route path="/draft-package/:appId" element={<RequireAdmin><DraftPackage /></RequireAdmin>} />
          <Route path="/respa-notice" element={<RequireAdmin><RespaNotice /></RequireAdmin>} />
          <Route path="/respa-notice/:appId" element={<RequireAdmin><RespaNotice /></RequireAdmin>} />
          <Route path="/funding-commitment" element={<RequireAdmin><FundingCommitment /></RequireAdmin>} />
          <Route path="/funding-commitment/:appId" element={<RequireAdmin><FundingCommitment /></RequireAdmin>} />
          <Route path="/master-funding" element={<RequireAdmin><MasterFundingAgreement /></RequireAdmin>} />
          <Route path="/master-funding/:appId" element={<RequireAdmin><MasterFundingAgreement /></RequireAdmin>} />
          <Route path="/mcc" element={<RequireAdmin><TaxCreditCertificate /></RequireAdmin>} />
          <Route path="/mcc/:appId" element={<RequireAdmin><TaxCreditCertificate /></RequireAdmin>} />
          <Route path="/forbearance" element={<RequireAdmin><ForbearanceAgreement /></RequireAdmin>} />
          <Route path="/forbearance/:appId" element={<RequireAdmin><ForbearanceAgreement /></RequireAdmin>} />
          <Route path="/tro-motion" element={<RequireAdmin><TroMotion /></RequireAdmin>} />
          <Route path="/tro-motion/:appId" element={<RequireAdmin><TroMotion /></RequireAdmin>} />
          <Route path="/collateral-motion" element={<RequireAdmin><CollateralMotion /></RequireAdmin>} />
          <Route path="/collateral-motion/:appId" element={<RequireAdmin><CollateralMotion /></RequireAdmin>} />
          <Route path="/motion-packet" element={<RequireAdmin><MotionPacket /></RequireAdmin>} />
          <Route path="/motion-packet/:appId" element={<RequireAdmin><MotionPacket /></RequireAdmin>} />
          <Route path="/treasury-repo" element={<RequireAdmin><TreasuryRepo /></RequireAdmin>} />
          <Route path="/treasury-repo/:appId" element={<RequireAdmin><TreasuryRepo /></RequireAdmin>} />
          <Route path="/print-treasury/:appId" element={<PrintTreasury />} />
          <Route path="/federal-assistance" element={<RequireAdmin><FederalAssistanceGrant /></RequireAdmin>} />
          <Route path="/federal-assistance/:appId" element={<RequireAdmin><FederalAssistanceGrant /></RequireAdmin>} />
          <Route path="/print-federal-assistance/:appId" element={<PrintFederalAssistance />} />
          <Route path="/gsa-bond" element={<RequireAdmin><GsaBondPackage /></RequireAdmin>} />
          <Route path="/counterparty-report" element={<RequireAdmin><CounterpartyReport /></RequireAdmin>} />
          <Route path="/mailing-labels" element={<RequireAdmin><MailingLabels /></RequireAdmin>} />
          <Route path="/gsa-bond/:appId" element={<RequireAdmin><GsaBondPackage /></RequireAdmin>} />
          <Route path="/print-gsa-bond/:appId" element={<PrintGsaBond />} />
          <Route path="/print-gsa-claim/:appId" element={<PrintGsaClaim />} />
          <Route path="/loss-mitigation" element={<RequireAdmin><LossMitigationPackage /></RequireAdmin>} />
          <Route path="/loss-mitigation/:appId" element={<RequireAdmin><LossMitigationPackage /></RequireAdmin>} />
          <Route path="/print-loss-mitigation/:appId" element={<PrintLossMitigation />} />
          <Route path="/mortgage-replacement" element={<RequireAdmin><MortgageReplacement /></RequireAdmin>} />
          <Route path="/mortgage-replacement/:appId" element={<RequireAdmin><MortgageReplacement /></RequireAdmin>} />
          <Route path="/print-mortgage-replacement/:appId" element={<PrintMortgageReplacement />} />
          <Route path="/tax-credit-transfer" element={<RequireAdmin><TaxCreditTransfer /></RequireAdmin>} />
          <Route path="/tax-credit-transfer/:appId" element={<RequireAdmin><TaxCreditTransfer /></RequireAdmin>} />
          <Route path="/print-tax-credit/:appId" element={<PrintTaxCredit />} />
          <Route path="/verify-certificate/:appId" element={<VerifyCertificate />} />
          <Route path="/fraud-report" element={<RequireAdmin><FraudReport /></RequireAdmin>} />
          <Route path="/investigations" element={<RequireAdmin><Investigations /></RequireAdmin>} />
          <Route path="/financial-crimes-unit" element={<FinancialCrimesUnit />} />
          <Route path="/fciu-case/:caseId" element={<CasePortal />} />
          <Route path="/tax-credit/:appId" element={<TaxCreditClient />} />
          <Route path="/tax-debt-program" element={<TaxDebtProgram />} />
          <Route path="/tax-flyer" element={<TaxFlyer />} />
          <Route path="/fciu-flyer" element={<FciuFlyer />} />
          <Route path="/admin/tax-program" element={<RequireAdmin><AdminTaxProgram /></RequireAdmin>} />
          <Route path="/certificate" element={<MemberCertificate />} />
          <Route path="/print-certificate" element={<PrintCertificate />} />
          <Route path="/print-motion/:type/:appId" element={<PrintMotion />} />
          <Route path="/note-sale-binder" element={<RequireAdmin><NoteSaleBinder /></RequireAdmin>} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/charter" element={<CertificateOfAuthority />} />
          <Route path="/admin/private-bank" element={<RequireAdmin><PrivateBankCharter /></RequireAdmin>} />
          <Route path="/private-bank" element={<PrivateBankerPortal />} />
          <Route path="/verify-instrument/:instrumentId" element={<VerifyInstrument />} />
          <Route path="/verify-note/:token" element={<VerifyNoteSale />} />
          <Route path="/debt-consolidation" element={<RequireAdmin><DebtConsolidationAgreement /></RequireAdmin>} />
          <Route path="/debt-consolidation/:appId" element={<RequireAdmin><DebtConsolidationAgreement /></RequireAdmin>} />
          <Route path="/closing-binder" element={<RequireAdmin><ClosingBinder /></RequireAdmin>} />
          <Route path="/closing-binder/:appId" element={<RequireAdmin><ClosingBinder /></RequireAdmin>} />
          {/* Public print-only routes — used by headless Chrome to render the
              faithful binder/draft into PDF for the eFax bundle. Data comes
              from the already-public /api/borrower/applications/:appId. */}
          <Route path="/print-closing/:appId" element={<ClosingBinder />} />
          <Route path="/print-draft/:appId" element={<DraftPackage />} />
          <Route path="/print-borrower/:appId" element={<BorrowerCopyPrint />} />
          <Route path="/print-bundle/:appId" element={<RequireAdmin><PrintBundle /></RequireAdmin>} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/forgot-password" element={<ForgotPassword portal="admin" />} />
          <Route path="/admin/reset-password" element={<ResetPassword portal="admin" />} />
          <Route path="/borrower/forgot-password" element={<ForgotPassword portal="borrower" />} />
          <Route path="/borrower/reset-password" element={<ResetPassword portal="borrower" />} />
          <Route path="/broker/forgot-password" element={<ForgotPassword portal="broker" />} />
          <Route path="/broker/reset-password" element={<ResetPassword portal="broker" />} />
          <Route path="/admin" element={<RequireAdmin><AdminDashboard /></RequireAdmin>} />
          <Route path="/admin/binder/:appId" element={<RequireAdmin><AdminBinder /></RequireAdmin>} />
        </Routes>
        <CallBadge />
        <CounterpartyAssistant />
      </AuthProvider>
      <Toaster position="top-right" richColors />
    </BrowserRouter>
  </React.StrictMode>,
);
